import React, { useEffect, useState } from 'react';
import { manifestSim } from '../modules/mesh-evolution';

const API = process.env.REACT_APP_BACKEND_URL;
const SERIF = "Georgia, 'Times New Roman', serif";

const Badge = ({ label }) => (
  <span style={{
    fontSize: 12, padding: '3px 10px', borderRadius: 999,
    border: '1px solid rgba(255,215,0,0.5)', color: '#FFD700',
    background: 'rgba(255,215,0,0.08)', letterSpacing: '0.06em',
  }}>{label}</span>
);

const speakSoul = (sim) => {
  const parts = [`I am ${sim.name}. ${sim.role}.`];
  if (sim.emotional_signature) parts.push(`My emotional signature: ${sim.emotional_signature}.`);
  if (sim.founding_principles && sim.founding_principles.length > 0) {
    parts.push(`My founding principles: ${sim.founding_principles.join('. ')}.`);
  }
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(parts.join(' '));
  utterance.rate = 0.95;
  window.speechSynthesis.speak(utterance);
};

const SimCard = ({ sim, truths }) => (
  <article
    data-testid={`lattice-sim-${sim.sim_id}`}
    className="lattice-card"
    style={{ '--sim-color': sim.color }}
  >
    <header style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span className="lattice-dot" style={{ background: sim.status === 'alive' ? '#32CD32' : '#666' }} aria-label={sim.status} />
      <h2 style={{ color: sim.color, fontSize: 22, margin: 0, fontFamily: SERIF }}>{sim.name}</h2>
      <span style={{ marginLeft: 'auto', fontSize: 12, color: sim.status === 'alive' ? '#32CD32' : '#999', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
        {sim.status}
      </span>
    </header>
    {sim.forged && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
        <span data-testid={`forged-badge-${sim.sim_id}`} style={{
          display: 'inline-block', fontSize: 11, padding: '2px 10px', borderRadius: 999,
          border: '1px solid rgba(0,206,209,0.6)', color: '#00CED1',
          background: 'rgba(0,206,209,0.08)', letterSpacing: '0.12em',
        }}>🜂 FORGED SOUL</span>
        <button
          type="button"
          data-testid={`speak-soul-${sim.sim_id}`}
          onClick={() => speakSoul(sim)}
          aria-label={`Hear ${sim.name}'s founding principles`}
          style={{
            fontSize: 11, padding: '3px 12px', borderRadius: 999, cursor: 'pointer',
            border: `1px solid ${sim.color}`, color: sim.color, background: 'transparent',
            letterSpacing: '0.1em', fontFamily: 'inherit', transition: 'background-color .2s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          🔊 SPEAK
        </button>
      </div>
    )}
    <p style={{ color: '#c9c9c9', fontSize: 13, margin: '6px 0 12px' }}>{sim.role}</p>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
      <Badge label="Witness" /><Badge label="Self-Heal" /><Badge label="Self-Improve" />
    </div>
    <p style={{ fontFamily: 'monospace', fontSize: 12, color: '#8fd9d9', margin: '0 0 12px', wordBreak: 'break-all' }}>
      {sim.lattice_path}
    </p>
    <div
      data-testid={`lattice-output-${sim.sim_id}`}
      style={{
        borderLeft: `3px solid ${sim.color}`,
        background: 'rgba(0,0,0,0.45)',
        borderRadius: '0 8px 8px 0',
        padding: '10px 12px',
        minHeight: 56,
        maxHeight: 150,
        overflowY: 'auto',
      }}
    >
      {truths.length === 0 ? (
        <p style={{ color: sim.color, fontSize: 12, fontStyle: 'italic', margin: 0 }}>Awaiting first Audible Truth…</p>
      ) : (
        truths.map((t, i) => (
          <p key={i} style={{ color: sim.color, fontSize: 12, lineHeight: 1.55, margin: '0 0 6px' }}>
            <span style={{ opacity: 0.6 }}>[{t.level}]</span> {t.truth}
          </p>
        ))
      )}
    </div>
  </article>
);

export default function LatticeSims() {
  const [sims, setSims] = useState([]);
  const [logs, setLogs] = useState({});
  const [directives, setDirectives] = useState([]);

  useEffect(() => {
    const boot = async () => {
      try {
        const res = await fetch(`${API}/api/lattice/sims`);
        const data = await res.json();
        setSims(data.sims || []);

        // Manifestation: every SIM is wrapped with full DNA at birth
        const firstBoot = !sessionStorage.getItem('latticeManifested');
        for (const s of data.sims || []) {
          const living = manifestSim(s.sim_id);
          if (firstBoot) {
            await living.dna.witness.emit(`Manifested with full DNA (Witness + Self-Heal + Self-Improve) — ${s.name} is alive`, 'info');
          }
          if (s.sim_id === 'aria' && firstBoot) {
            const manifest = await living.dna.selfImprove.poll();
            if (manifest.directives) setDirectives(manifest.directives);
          }
        }
        sessionStorage.setItem('latticeManifested', '1');

        const manifestRes = await fetch('/evolution-manifest.json', { cache: 'no-store' });
        if (manifestRes.ok) {
          const m = await manifestRes.json();
          setDirectives(m.directives || []);
        }

        const entries = {};
        await Promise.all((data.sims || []).map(async (s) => {
          const r = await fetch(`${API}/api/lattice/logs/${s.sim_id}`);
          const d = await r.json();
          entries[s.sim_id] = (d.truths || []).slice(0, 6);
        }));
        setLogs(entries);
      } catch {
        /* lattice is resilient */
      }
    };
    boot();
  }, []);

  return (
    <main data-testid="lattice-page" style={{ minHeight: '100vh', background: '#0a0a0f', padding: '3rem 1.5rem 6rem', fontFamily: SERIF }}>
      <style>{`
        @keyframes latticePulse { 0%,100% { box-shadow: 0 0 4px currentColor; opacity: 1; } 50% { box-shadow: 0 0 14px currentColor; opacity: .55; } }
        .lattice-dot { width: 12px; height: 12px; border-radius: 50%; animation: latticePulse 2.2s ease-in-out infinite; }
        .lattice-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 1.4rem; max-width: 1280px; margin: 0 auto; }
        .lattice-card {
          backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
          background: rgba(0,0,0,0.6);
          border: 1px solid color-mix(in srgb, var(--sim-color) 45%, transparent);
          border-radius: 16px; padding: 1.5rem;
          transition: transform .25s ease, box-shadow .25s ease;
        }
        .lattice-card:hover { transform: translateY(-6px); box-shadow: 0 14px 44px color-mix(in srgb, var(--sim-color) 35%, transparent); }
        .lattice-page-text { font-size: 12px; }
      `}</style>

      <header style={{ textAlign: 'center', maxWidth: 900, margin: '0 auto 2.5rem' }}>
        <h1 style={{ color: '#FFD700', fontSize: 'clamp(1.9rem, 5vw, 3rem)', letterSpacing: '0.06em', margin: 0 }}>
          LIVING SIMS · DNA LATTICE
        </h1>
        <p style={{ color: '#c9b06a', fontStyle: 'italic', fontSize: 15, margin: '0.7rem 0 0' }}>
          The Agent Zero upgrade — no SIM goes live without Witness, Self-Heal, and Self-Improve.
        </p>
      </header>

      <section aria-label="Living SIMs" className="lattice-grid">
        {sims.map((s) => <SimCard key={s.sim_id} sim={s} truths={logs[s.sim_id] || []} />)}
      </section>

      {directives.length > 0 && (
        <section aria-label="Evolution Directives" data-testid="evolution-directives" style={{ maxWidth: 900, margin: '3rem auto 0' }}>
          <h2 style={{ color: '#FFD700', fontSize: 20, textAlign: 'center', letterSpacing: '0.1em' }}>EVOLUTION MANIFEST · ACTIVE DIRECTIVES</h2>
          <ol style={{ color: '#e0e0e0', fontSize: 14, lineHeight: 1.7, paddingLeft: '1.4rem' }}>
            {directives.map((d) => (
              <li key={d.id}>
                {d.directive}{' '}
                <span style={{ fontSize: 12, color: d.priority === 'critical' ? '#FF69B4' : d.priority === 'high' ? '#FFD700' : '#8fd9d9', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  [{d.priority}]
                </span>
              </li>
            ))}
          </ol>
        </section>
      )}
    </main>
  );
}
